// src/components/pages/profile/ProfileSettingsPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Box,
  TextField,
  Typography,
  Snackbar,
  Button,
  Alert,
  CircularProgress,
  Backdrop,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MuiAlert from "@mui/material/Alert";

import ImageUploader from "../../features/profile/ImageUploader";
import { APP_SHELL_LAYOUT } from "../../../constants/constants";

import useProfileEditor from "../../../hooks/useProfileEditor";
import AvatarPicker from "../../features/profile/AvatarPicker";
import AffiliatedRewardsSection from "../../features/profile/AffiliatedRewardsSection";

const ProfileSettingsPage = () => {
  const {
    isLoading,
    saveChangesLoading,
    user,
    userId,
    formData,
    profileTextMaxLength,
    setField,
    saveProfile,
    uploadAvatar,
    removeAvatar,
    snackbar,
    closeSnackbar,
    affiliatePrograms,
    upsertAffiliateReward,
    removeAffiliateReward,
  } = useProfileEditor();

  const [uploadTrigger, setUploadTrigger] = useState(0);
  const [activeTab, setActiveTab] = useState("basic");

  // Dirty tracking (ignores profileImage)
  const baselineRef = useRef(null);
  const userEditedRef = useRef(false);

  const textFieldStyles = (theme) => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.25,
      "&.MuiInputBase-readOnly": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.035)
            : alpha(theme.palette.primary.main, 0.03),
      },
      "&.Mui-disabled": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.035)
            : alpha(theme.palette.primary.main, 0.03),
      },
    },
  });

  const normalizeRewards = useCallback((rewards) => {
    if (!Array.isArray(rewards)) return [];
    return [...rewards]
      .map((r) => ({ ...r }))
      .sort((a, b) => {
        const ak = String(a.programId || a.programName || a.id || "");
        const bk = String(b.programId || b.programName || b.id || "");
        return ak.localeCompare(bk);
      });
  }, []);

  const snapshotForDirtyCheck = useCallback(
    (data) => {
      if (!data) return null;
      return {
        displayName: data.displayName || "",
        bio: data.bio || "",
        affiliatedRewards: normalizeRewards(data.affiliatedRewards),
      };
    },
    [normalizeRewards]
  );

  // While the user has not edited anything, keep baseline synced to incoming profile data (prevents "dirty on load")
  useEffect(() => {
    if (isLoading || !user || !formData) return;
    if (userEditedRef.current) return;
    baselineRef.current = snapshotForDirtyCheck(formData);
  }, [isLoading, user, formData, snapshotForDirtyCheck]);

  const isDirty = useMemo(() => {
    const baseline = baselineRef.current;
    if (!baseline) return false;
    const current = snapshotForDirtyCheck(formData);
    return JSON.stringify(baseline) !== JSON.stringify(current);
  }, [formData, snapshotForDirtyCheck]);

  // After a successful save, reset baseline and allow future server sync again
  useEffect(() => {
    if (snackbar?.open && snackbar?.severity === "success") {
      baselineRef.current = snapshotForDirtyCheck(formData);
      userEditedRef.current = false;
    }
  }, [snackbar, formData, snapshotForDirtyCheck]);

  const setFieldWithDirtyFlag = useCallback(
    (field, value) => {
      userEditedRef.current = true;
      setField(field, value);
    },
    [setField]
  );

  // IMPORTANT: keep these wrappers signature-flexible so AffiliatedRewardsSection
  // can call them however it wants (event, (programId, value), etc.)
  const upsertAffiliateRewardWithDirtyFlag = useCallback(
    (...args) => {
      userEditedRef.current = true;
      return upsertAffiliateReward(...args);
    },
    [upsertAffiliateReward]
  );

  const removeAffiliateRewardWithDirtyFlag = useCallback(
    (...args) => {
      userEditedRef.current = true;
      return removeAffiliateReward(...args);
    },
    [removeAffiliateReward]
  );

  const canSave = isDirty && !saveChangesLoading;

  const handleSave = async () => {
    if (!canSave) return;
    await saveProfile();
  };

  if (isLoading || !user) {
    return (
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="h6">Loading profile...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Container
        maxWidth="md"
        sx={{
          mt: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 5 },
          pb: "calc(env(safe-area-inset-bottom, 0px) + 90px)",
        }}
      >
        <Backdrop
          sx={(theme) => ({ color: theme.colors.white, zIndex: theme.zIndex.drawer + 1 })}
          open={saveChangesLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <ImageUploader
          uploadTrigger={uploadTrigger}
          onImageUpload={uploadAvatar}
          userId={userId}
        />

        <Box sx={{ px: { xs: 0.5, sm: 0 }, mb: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2.5, sm: 3 }}
            alignItems={{ xs: "center", sm: "flex-start" }}
            sx={{ mb: 3 }}
          >
            <AvatarPicker
              src={formData.profileImage}
              onEdit={() => setUploadTrigger((t) => t + 1)}
              onRemove={removeAvatar}
            />

            <Box sx={{ flex: 1, width: "100%" }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Profile settings
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                Update your photo, profile details, and rewards info.
              </Typography>
              <Button
                component={RouterLink}
                to="/me"
                size="small"
                variant="outlined"
                sx={{ mt: 1.1, textTransform: "none", fontWeight: 700 }}
              >
                Open profile
              </Button>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ px: { xs: 0.5, sm: 0 }, mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            centered
            sx={(theme) => ({
              mb: 3,
              "& .MuiTab-root": {
                minHeight: 40,
                fontWeight: 700,
                textTransform: "none",
                color: theme.palette.mode === "dark" ? "text.secondary" : undefined,
              },
              "& .Mui-selected": {
                color: `${theme.colors.blue} !important`,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: theme.colors.blue,
              },
            })}
          >
            <Tab
              label="Basic info"
              value="basic"
              disableRipple
              sx={{ fontSize: "1rem" }}
            />
            <Tab
              label="Affiliate rewards"
              value="rewards"
              disableRipple
              sx={{ fontSize: "1rem" }}
            />
          </Tabs>

          {activeTab === "basic" ? (
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Display Name"
                name="displayName"
                value={formData.displayName}
                onChange={(e) =>
                  setFieldWithDirtyFlag("displayName", e.target.value)
                }
                inputProps={{ maxLength: profileTextMaxLength }}
                helperText={`Up to ${profileTextMaxLength} characters. Letters, accents, spaces, apostrophes, hyphens, and periods are allowed.`}
                variant="outlined"
                sx={(theme) => textFieldStyles(theme)}
              />

              <TextField
                fullWidth
                label="Bio"
                name="bio"
                value={formData.bio}
                onChange={(e) => setFieldWithDirtyFlag("bio", e.target.value)}
                variant="outlined"
                multiline
                minRows={4}
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                sx={(theme) => ({
                  ...textFieldStyles(theme),
                  "& .MuiOutlinedInput-root": {
                    ...textFieldStyles(theme)["& .MuiOutlinedInput-root"],
                    borderRadius: 1.25,
                    alignItems: "flex-start",
                  },
                })}
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                value={formData.email}
                variant="outlined"
                InputProps={{ readOnly: true }}
                helperText="Your sign-in email can’t be edited here."
                sx={(theme) => textFieldStyles(theme)}
              />
            </Stack>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 2 }}
              >
                Add rewards info for programs you participate in.
              </Typography>

              {affiliatePrograms.length > 0 ? (
                <AffiliatedRewardsSection
                  programs={affiliatePrograms}
                  rewards={formData.affiliatedRewards}
                  onUpsert={upsertAffiliateRewardWithDirtyFlag}
                  onRemove={removeAffiliateRewardWithDirtyFlag}
                  textFieldStyles={textFieldStyles}
                />
              ) : (
                <Alert severity="info">
                  No rewards programs are available yet. Check back soon.
                </Alert>
              )}
            </>
          )}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={closeSnackbar}
        >
          <MuiAlert
            onClose={closeSnackbar}
            variant="filled"
            severity={snackbar.severity}
            sx={(theme) => ({
              width: "100%",
              ...(snackbar.severity === "success"
                ? {
                    backgroundColor: theme.colors.green,
                    color: theme.palette.common.white,
                  }
                : {}),
            })}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Container>

      {/* Full-width Bottom Action Bar (top border only) */}
      <Box
        sx={(theme) => ({
          position: "fixed",
          left: 0,
          right: 0,
          bottom: APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS,
          zIndex: theme.zIndex.appBar,
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.9)
              : alpha(theme.palette.background.paper, 0.94),
          backdropFilter: "blur(16px)",
          borderTop: "1px solid",
          borderTopColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.08)
              : alpha(theme.palette.primary.main, 0.08),
          px: 2,
          py: 1.5,
        })}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            sx={(theme) => ({
              fontWeight: 700,
              border: 0,
              backgroundColor: canSave
                ? theme.colors.blue
                : theme.palette.action.disabledBackground,
              color: canSave ? theme.colors.white : theme.palette.action.disabled,
              px: 3,
              py: 1.25,
              width: "100%",
              maxWidth: 520,
            })}
          >
            Save Changes
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ProfileSettingsPage;
